// Picks the route-transition ENGINE at each mount site from the `scroll_transitions_enabled` app flag: the
// scroll-snap pager (the default, see server/common/feature-flags.ts) or the imperative gesture controller
// (the fallback, flag OFF). The two engines render DIFFERENT DOM (a live layer vs. a scroll-snap scroller with
// slots), so the choice must be settled at SSR time and read the same on both sides — hence useSuspenseQuery
// over the flag, prefetched in the loader of each route that mounts this (see __root.tsx / admin.tsx), so it
// never suspends the document after hydration.
//
// The scroll engine is code-split, to keep its ~1200 lines off the entry/hydration critical path (the leak
// bundle-budget guards against). The server still renders it — lazy resolves during SSR — and the client
// hydrates that HTML in place once the chunk loads; the <Suspense> fallback only ever shows on a client-side
// mount before the chunk is cached, where the gesture engine renders the same route content.
//
// The flag read itself is cosmetic chrome above every route boundary, so it must never take the shell down:
// GracefulBoundary degrades a rejected flag query (e.g. a transient 503 past its retries on client nav) to
// the gesture engine instead of the route error screen.

import { lazy, Suspense } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { trpc } from '@/main'
import { GracefulBoundary } from '@/components/custom/graceful-boundary'
import { GestureTransitionOutlet } from './gesture-transition-outlet'
import type { TransitionOutletProps } from './surface.config'

const ScrollTransitionOutlet = lazy(() =>
  import('./scroll-transition-outlet').then((m) => ({ default: m.ScrollTransitionOutlet })),
)

export function TransitionOutlet(props: TransitionOutletProps) {
  return (
    <GracefulBoundary fallback={<GestureTransitionOutlet {...props} />}>
      <EngineChooser {...props} />
    </GracefulBoundary>
  )
}

function EngineChooser(props: TransitionOutletProps) {
  const { data: useScroll } = useSuspenseQuery(trpc.common.scrollTransitionsEnabled.queryOptions())
  if (!useScroll) return <GestureTransitionOutlet {...props} />
  return (
    <Suspense fallback={<GestureTransitionOutlet {...props} />}>
      <ScrollTransitionOutlet {...props} />
    </Suspense>
  )
}
